import React, { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { connect } from "react-redux";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  Avatar,
  useMediaQuery,
  Switch,
  useTheme,
} from "@mui/material";

// MUI Icons
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ScaleIcon from "@mui/icons-material/Scale";
import DescriptionIcon from "@mui/icons-material/Description";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";

import logo from "../../logo_shl.png";
import auth from "../../Services/loginService";
const jcc = require("json-case-convertor");

const drawerWidth = 300;

function Navbar(props) {
  const navigate = useNavigate();
  const { page } = useParams();
  const theme = useTheme(); // מקבל את ה-Theme המעודכן (Light/Dark) מה-App
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const userDetails = jcc.snakeCaseKeys(props.userDetails);
  const pat = props.selectedPatient;
  const isDarkMode = props.darkMode;
  const menuItems = [
    {
      id: "activities",
      title: "פעילויות",
      icon: <PhoneInTalkIcon />,
      show: pat && props.activitiesResults?.length > 0,
    },
    {
      id: "invoices",
      title: "חשבוניות",
      icon: <ReceiptLongIcon />,
      show: pat && props.invoices?.length > 0,
    },
    {
      id: "hafnayot",
      title: "הפניות",
      icon: <AssignmentIcon />,
      show: pat && props.hafnayot?.length > 0,
    },
    {
      id: "ecg",
      title: "אק''ג",
      icon: <MonitorHeartIcon />,
      show: props.ecgsResults?.length > 0,
    },
    {
      id: "bloodP",
      title: "לחץ דם",
      icon: <AssessmentIcon />,
      show: pat && props.bloodPResults?.length > 0,
    },
    {
      id: "weight",
      title: "משקל",
      icon: <ScaleIcon />,
      show: pat && props.weightResults?.length > 0,
    },
    {
      id: "cardioR",
      title: "דוחות Cardio-R",
      icon: <DescriptionIcon />,
      show: props.cardioRResults?.length > 0,
    },
  ];

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const disconnect = async () => {
    await auth.logout();
    props.logout(undefined);
  };

  // תוכן התפריט
  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper", // שימוש בצבע מה-Theme
        color: "text.primary", // שימוש בצבע טקסט מה-Theme
      }}
    >
      {/* אזור הלוגו */}
      <Box sx={{ pt: 2, textAlign: "center" }}>
        <img
          src={logo}
          alt="SHL Logo"
          style={{
            height: "50px",
            maxWidth: "100%",
            // היפוך צבעי הלוגו במצב כהה כדי שיראו אותו על שחור
            filter: isDarkMode ? "brightness(0) invert(1)" : "none",
          }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* פרטי מטופל */}
      {pat && (
        <Box sx={{ px: 1, textAlign: "center" }}>
          <Avatar
            sx={{
              bgcolor: theme.palette.primary.main,
              margin: "0 auto 8px",
              width: 45,
              height: 45,
              color: "#fff",
            }}
          >
            {pat.firstname?.[0]}
          </Avatar>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {pat.firstname} {pat.lastname}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ת"ז: {pat.custid}
          </Typography>
        </Box>
      )}

      {/* רשימת הניווט */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {!pat && (
          <ListItem
            disablePadding
            component={Link}
            to="../patients"
            sx={{ color: "inherit", textDecoration: "none" }}
          >
            <ListItemButton
              selected={page === "patients"}
              sx={{ borderRadius: "8px", mb: 0.5 }}
            >
              <ListItemIcon>
                <PeopleOutlineIcon
                  color={page === "patients" ? "primary" : "inherit"}
                />
              </ListItemIcon>
              <ListItemText
                primary="דף לקוחות"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </ListItem>
        )}

        {menuItems
          .filter((item) => item.show)
          .map((item) => (
            <ListItem
              key={item.id}
              disablePadding
              component={Link}
              to={`../${item.id}`}
              sx={{ color: "inherit", textDecoration: "none" }}
            >
              <ListItemButton
                selected={page === item.id}
                onClick={() => isMobile && setMobileOpen(false)}
                sx={{ borderRadius: "8px", mb: 0.5, py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 35 }}>
                  {React.cloneElement(item.icon, {
                    color: page === item.id ? "primary" : "inherit",
                  })}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: "0.8rem", // שנה ל-12px או 0.8rem אם אתה רוצה אפילו יותר קטן
                    fontWeight: page === item.id ? 700 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}

        {pat && userDetails?.ipermit !== 1 && (
          <ListItem disablePadding sx={{ mt: 2 }}>
            <ListItemButton
              onClick={() => {
                props.updateSelectedPatient(undefined);
                navigate("../patients");
              }}
              sx={{
                borderRadius: "8px",
                border: `1px dashed ${theme.palette.divider}`,
              }}
            >
              <ListItemIcon>
                <ArrowBackIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="חזרה לכל הלקוחות"
                primaryTypographyProps={{ fontSize: "0.9rem" }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </List>

      {/* תחתית התפריט - מצב כהה והתנתקות */}
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 1 }} />

        <ListItem disablePadding>
          <ListItemButton
            sx={{ borderRadius: "8px" }}
            onClick={() => props.toggleDarkMode(!isDarkMode)}
          >
            <ListItemIcon>
              {isDarkMode ? (
                <DarkModeIcon color="primary" sx={{ height: "0.8rem" }} />
              ) : (
                <LightModeIcon sx={{ height: "0.8rem" }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary="מצב כהה"
              primaryTypographyProps={{ fontWeight: 600, fontSize: "0.8rem" }}
            />
            <Switch
              edge="end"
              checked={isDarkMode}
              onChange={(e) => props.toggleDarkMode(e.target.checked)}
              size="small"
            />
          </ListItemButton>
        </ListItem>

        <ListItemButton
          onClick={disconnect}
          sx={{ borderRadius: "8px", color: theme.palette.error.main, mt: 0 }}
        >
          <ListItemIcon>
            <LogoutIcon
              sx={{ color: theme.palette.error.main, fontSize: "1.2rem" }}
            />
          </ListItemIcon>
          <ListItemText
            primary="התנתקות"
            primaryTypographyProps={{ fontWeight: 600, fontSize: "0.8rem" }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile Header */}
      {isMobile && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            left: 0,
            zIndex: 1000,
            height: 60,
            bgcolor: "background.paper",
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            px: 2,
            direction: "rtl",
          }}
        >
          <IconButton onClick={handleDrawerToggle} edge="start" color="primary">
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              textAlign: "center",
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            שחל רשומה רפואית
          </Typography>
          <Box sx={{ width: 40 }} />
        </Box>
      )}

      {/* Side Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          anchor="left"
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "background.paper", // מבטיח שצבע הרקע של ה-Drawer ישתנה
              backgroundImage: "none", // מבטל "shading" של MUI במצב כהה
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          anchor="left"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRight: "none",
              bgcolor: "background.paper", // כאן קורה הקסם של החלפת הצבע
              backgroundImage: "none",
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Spacer למובייל */}
      {isMobile && <Box sx={{ height: 60 }} />}
    </>
  );
}

const mapStateToProps = (state) => ({
  selectedPatient: state.selectedPatient,
  userDetails: state.userDetails,
  bloodPResults: state.bloodPResults,
  weightResults: state.weightResults,
  ecgsResults: state.ecgsResults,
  activitiesResults: state.activitiesResults,
  cardioRResults: state.cardioRResults,
  invoices: state.invoices,
  hafnayot: state.hafnayot,
  darkMode: state.darkMode,
});

const mapDispatchToProps = (dispatch) => ({
  updateSelectedPatient: (pat) =>
    dispatch({ type: "UPDATE_SELECTED_PATIENT", payload: pat }),
  logout: (newDetails) => dispatch({ type: "LOGOUT", payload: newDetails }),
  toggleDarkMode: (val) => dispatch({ type: "TOGGLE_DARK_MODE", payload: val }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);
